<!-- A dropdown widget to select a Group to upload content to.

The Groups that can be selected are based on the User's GroupAccesses
with the can_manage_content=True flag.

The widget is pre-populated if possible, either when there is just a
single choice or when the optional this.contentToReplace dictates a
Group to preselect.

The widget is hidden when there is no or just a single choice, since
then we don't need to do anything.

-->

<template>
  <div>
    <v-select
      v-show="showForSingleUploadGroup || filteredUploadgroups.length > 1"
      :key="selectedUploadGroupId + vSelectRenderKey"
      v-model="model"
      :items="filteredUploadgroups"
      :label="$t('group')"
      :hint="hint"
      persistent-hint
      item-text="name"
      item-value="id"
      :class="{'v-select-highlight-hint': selectedUploadGroupId === null}"
      data-cy="upload-group-select"
    />
    <v-alert
      v-if="!loading"
      :value="filteredUploadgroups.length === 0"
      type="warning"
    >
      {{ noUploadGroupsWarning }}
    </v-alert>
  </div>
</template>

<script>
import uploadGroupSelect from '@/mixins/uploadGroupSelect';

export default {
  mixins: [uploadGroupSelect],
  props: {
    selectedUploadGroupId: {
      type: Number,
      // Note: We'd love to use required: true, but then we cannot
      //   also accept null, which is why instead we use it as a default.
      //   This widget is however pretty useless if the prop is not passed.
      default: null,
    },
    /**
     * We hide the widget by default when there is just one choice.
     *
     * This can be used to show it anyways.
     */
    showForSingleUploadGroup: {
      type: Boolean,
      default: false,
    },
    /**
     * Optional message to show user to confirm his change.
     *
     * If not confirmed, do not select a new uploadgroup.
     */
    confirmationMessage: {
      type: String,
      default: null,
    },
    /** Optional filter function for the displayed groups list. */
    groupFilter: {
      type: Function,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hint: {
      type: String,
      default: function () {
        return this.$t('chooseUploadGroup') + '.';
      },
    },
  },
  data() {
    return {
      vSelectRenderKey: 0,
    };
  },
  computed: {
    model: {
      get() {
        return this.selectedUploadGroupId;
      },
      set(groupId) {
        this.onUploadGroupIdSelected(groupId);
      },
    },
    filteredUploadgroups() {
      if (!this.groupFilter) {
        return this.uploadgroups;
      }
      return this.groupFilter(this.uploadgroups);
    },
    noUploadGroupsWarning() {
      return this.$store.getters.userIsBeingManaged
        ? this.$t('noUploadGroupsForTemplateAdmin')
        : this.$t('noUploadGroupsForTemplate');
    },
  },
  watch: {
    /**
     * It takes a moment to fetch the uploadgroups:
     * Make sure the dropdown is populated once we have data.
     */
    uploadgroups(val) {
      this.preselectUploadGroupIfPossible();
    },
  },
  mounted() {
    this.preselectUploadGroupIfPossible();
  },
  methods: {
    preselectUploadGroupIfPossible() {
      const alreadySelected = this.selectedUploadGroupId !== null;
      if (alreadySelected) {
        return;
      }
      const initialUploadGroupId = this.getInitialUploadgroupId();
      this.onUploadGroupIdSelected(initialUploadGroupId);
    },
    onUploadGroupIdSelected(groupId) {
      if (this.confirmationMessage && groupId !== this.selectedUploadGroupId) {
        if (!confirm(this.confirmationMessage)) {
          // Revert v-select change by forcing a re-render.
          this.vSelectRenderKey += 1;
          return;
        }
      }
      this.$emit('update:selectedUploadGroupId', groupId);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/UploadGroupSelect.vue